/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { rem, useMantineTheme, Text, Group } from '@mantine/core';
import { RiCircleFill, RiErrorWarningLine } from '@remixicon/react';
import { useEffect, useState, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useEndpointEnableLogging } from '@fai/friendli-suite/features/products/dedicated-endpoints/endpoints/use-endpoint-enable-logging';
import { useEndpointVersionInfo } from '@fai/friendli-suite/features/products/dedicated-endpoints/endpoints/versions/use-endpoint-version-info';
import Paths from '@fai/friendli-suite/features/routes/paths';
import { DedicatedEndpointVersionStatus } from '@fai/friendli-suite/generated/client/graphql';
import { useFlags } from '@fai/friendli-suite/shared/launch-darkly';
import DEProjectSubLayout from '@fai/friendli-suite/views/layout/product/dedicated-endpoints/projects/sub-layout';
import { NavigationTabs } from '@fai/ui/components/tabs/navigation-tabs';
import Tag from '@fai/ui/components/tag';
import { TextVariants } from '@fai/ui/components/typography/variants';

import EndpointDetailHeader from './endpoint-detail-header';

export default function ProjectEndpointDetailLayout() {
  const { t } = useTranslation();
  const { teamId = '', projectId = '', endpointId = '' } = useParams();
  const theme = useMantineTheme();
  const { dedicatedEndpointMetrics, dedicatedEndpointLogs, dedicatedEndpointVersions } = useFlags();

  const [latestVersionStatusState, setLatestVersionStatusState] =
    useState<DedicatedEndpointVersionStatus | null>(null);
  const [versionPulse, setVersionPulse] = useState<boolean>(false);

  const { latestVersion } = useEndpointVersionInfo();

  useEffect(() => {
    if (
      (latestVersionStatusState === DedicatedEndpointVersionStatus.Applying ||
        latestVersionStatusState === DedicatedEndpointVersionStatus.Pending) &&
      latestVersion?.status === DedicatedEndpointVersionStatus.Applied
    ) {
      setVersionPulse(true);
    }

    setLatestVersionStatusState(latestVersion?.status ?? null);
  }, [latestVersion?.status]);

  const { enableContentLogging } = useEndpointEnableLogging();

  const basePath = Paths.team(teamId)
    .dedicatedEndpoints.project.detail(projectId)
    .endpoint.detail(endpointId);

  const tabValues: ComponentProps<typeof NavigationTabs>['values'] = [
    {
      label: t('dedicated_endpoints.project.overview'),
      path: basePath.overview,
    },
    {
      label: t('dedicated_endpoints.project.playground'),
      path: basePath.playground,
    },
    ...(dedicatedEndpointMetrics
      ? [
          {
            label: t('dedicated_endpoints.project.metrics'),
            path: basePath.metrics,
          },
        ]
      : []),
    ...(dedicatedEndpointLogs && enableContentLogging
      ? [
          {
            label: t('dedicated_endpoints.project.logs'),
            path: basePath.logs,
          },
        ]
      : []),
    ...(dedicatedEndpointVersions
      ? [
          {
            label: t('dedicated_endpoints.project.versions'),
            path: basePath.versions,
            onClick: () => setVersionPulse(false),
          },
        ]
      : []),
  ];

  return (
    <DEProjectSubLayout
      headerSlot={<EndpointDetailHeader />}
      tabSlot={
        <NavigationTabs
          values={tabValues}
          renderTab={({ label, path }) => (
            <Group gap={rem(6)}>
              <Text variant={TextVariants.TitleSm}>{label}</Text>

              {path === basePath.versions && (
                <>
                  <Tag
                    size="2xs"
                    bg={theme.colors.gray[9]}
                    c={theme.pf.color.fg.white}
                    style={{ border: 'none', borderRadius: theme.pf.radius.sm }}
                  >
                    {`${t('dedicated_endpoints.project.beta_tag')}`}
                  </Tag>

                  {latestVersion?.status === 'CANCELLED' ? (
                    <RiErrorWarningLine color={theme.pf.color.fg.danger.default.base} size={16} />
                  ) : versionPulse ? (
                    <RiCircleFill color={theme.pf.color.fg.cloudblue.default.base} size={8} />
                  ) : null}
                </>
              )}
            </Group>
          )}
          preventScrollReset={true}
        />
      }
    />
  );
}
